/** biome-ignore-all lint/suspicious/noExplicitAny: allow any */

declare global {
  interface Window {
    webview: any;
    webkit?: any;
    appVersion: string;
    [key: string]: any;
  }
}

// TODO: TI-173 (https://evitetracking.atlassian.net/browse/TI-173) to write tests for this bridge function
export const runWebviewToAppBridge = (channel: string, message: any) => {
  if (channel in window) {
    // flutter; do not even try ios/android; return after trying to post message
    try {
      window[channel]?.postMessage?.(message);
    } catch (e) {
      evite.error(e);
    }
    return;
  }

  // ios
  try {
    window.webkit?.messageHandlers?.[channel]?.postMessage?.(message);
  } catch (e) {
    evite.error(e);
  }

  // android
  try {
    window.webview?.[channel]?.(message);
  } catch (e) {
    evite.error(e);
  }
};

const BASE_WEBVIEW_CHANNELS = [
  'fabricCreateOnNav', // invitation create/edit flow
  'viewInvitationSetRsvp', // view invitation flow
];

export const isFabricUniversalWebview = (extraChannelsToWatch = []) =>
  [...BASE_WEBVIEW_CHANNELS, ...extraChannelsToWatch].some((channel) => window?.[channel]);
export const isFabricIOSWebview = (extraChannelsToWatch = []) =>
  [...BASE_WEBVIEW_CHANNELS, ...extraChannelsToWatch].some(
    (channel) => window?.webkit?.messageHandlers?.[channel]
  );
export const isFabricAndroidWebview = (extraChannelsToWatch = []) =>
  [...BASE_WEBVIEW_CHANNELS, ...extraChannelsToWatch].some((channel) => window?.webview?.[channel]);

export const isFabricWebview = (extraChannelsToWatch = []) =>
  !!(
    isFabricUniversalWebview(extraChannelsToWatch) ||
    isFabricIOSWebview(extraChannelsToWatch) ||
    isFabricAndroidWebview(extraChannelsToWatch)
  );

export const isLegacyFabricWebview = () =>
  !!(window?.webkit?.messageHandlers?.fabricCreateOnNav || window?.webview?.fabricCreateOnNav);
/**
 * Compares two semantic version strings (e.g., "1.2.3" vs "1.3.0").
 *
 * @param {string} a - The first version string to compare (format: "x[.y[.z]]").
 * @param {string} b - The second version string to compare (format: "x[.y[.z]]").
 * @returns {-1 | 0 | 1}
 *    - Returns `1` if version `a` is greater (newer).
 *    - Returns `-1` if version `b` is greater (newer).
 *    - Returns `0` if both versions are equal.
 */
function semverCmp(a: string, b: string): -1 | 0 | 1 {
  const pa = a.split('.');
  const pb = b.split('.');
  for (let i = 0; i < 3; i++) {
    const na = Number(pa[i]);
    const nb = Number(pb[i]);
    if (na > nb) return 1;
    if (nb > na) return -1;
    if (!isNaN(na) && isNaN(nb)) return 1;
    if (isNaN(na) && !isNaN(nb)) return -1;
  }
  return 0;
}

/**
 * Checks if version is greater than or equal to `version`
 * @param version string for the version check in format 72.10.0
 * @returns {boolean} true if `version` is greater than or equal to the current webview version, false otherwise
 */
export const isAppVersionGte = (version: string): boolean => {
  if (!window.appVersion || window.appVersion === '0') return false;
  try {
    return isFabricWebview() && semverCmp(version, window.appVersion) <= 0;
  } catch {
    return false;
  }
};
